import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import JourneyService from "../services/JourneyService";
import {
  PHASE1_GATES,
  PHASE1_REFERENCE_18_PROBLEM_KEY,
  getPhaseOneGateLabel,
  getPhaseOneGenderPrompt,
  getPhaseOneReadyButton,
  isPhaseOnePlaceholderGateKey,
} from "../phase1/config";
import { dirForLang, normalizeLang } from "../i18n";
import "../phase1/phase1Entry.css";

const COPY = {
  he: {
    brand: "INNERVOICE",
    body: "כדי שהמילים ירגישו מדויקות,\nאפשר לבחור איך לפנות אליך.",
    genderMale: "זכר",
    genderFemale: "נקבה",
    genderError: "כדי להמשיך, בחר/י זכר או נקבה.",
    back: "לבחור מחדש",
    saving: "שומר...",
  },
  en: {
    brand: "INNERVOICE",
    body: "So the words feel accurate,\nyou can choose how we address you.",
    genderMale: "Male",
    genderFemale: "Female",
    genderError: "Choose male or female to continue.",
    back: "Choose again",
    saving: "Saving...",
  },
  es: {
    brand: "INNERVOICE",
    body: "Para que las palabras se sientan acertadas,\npuedes elegir cómo te hablamos.",
    genderMale: "Masculino",
    genderFemale: "Femenino",
    genderError: "Elige masculino o femenino para continuar.",
    back: "Elegir de nuevo",
    saving: "Guardando...",
  },
};

function getCopy(lang) {
  return COPY[lang] || COPY.he;
}

export default function EntryReady() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, updateProfile } = useAuth();
  const lang = normalizeLang(params.get("lang") || "he");
  const gateKey = params.get("gate") || "";
  const dir = dirForLang(lang);
  const copy = getCopy(lang);
  const gate = useMemo(() => PHASE1_GATES.find((item) => item.key === gateKey) || null, [gateKey]);
  const currentGender = useMemo(() => {
    const value = String(user?.gender || "").toLowerCase();
    return value === "male" || value === "female" ? value : "";
  }, [user?.gender]);
  const [selectedGender, setSelectedGender] = useState(currentGender);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    try {
      localStorage.setItem("innervoice_lang", lang);
    } catch {
      // ignore
    }
  }, [lang]);

  useEffect(() => {
    setSelectedGender(currentGender);
  }, [currentGender]);

  useEffect(() => {
    if (!gate) {
      navigate(`/phase1/select?lang=${lang}`, { replace: true });
    }
  }, [gate, lang, navigate]);

  if (!gate) return null;

  const needsGender = !selectedGender;
  const buttonLabel = getPhaseOneReadyButton(lang, selectedGender || user?.gender || "other");

  const handleContinue = async () => {
    setError("");
    if (needsGender) {
      setError(copy.genderError);
      return;
    }

    if (isPhaseOnePlaceholderGateKey(gate.key)) {
      navigate(`/journey/select?lang=${encodeURIComponent(lang)}&fresh=1`);
      return;
    }

    setSaving(true);
    try {
      if (selectedGender && selectedGender !== currentGender) {
        await updateProfile({ gender: selectedGender });
      }

      try {
        localStorage.setItem(`disclaimerAccepted:${PHASE1_REFERENCE_18_PROBLEM_KEY}`, "true");
      } catch {
        // ignore
      }

      const existingMoment = JourneyService.getLatestPresenceMoment(lang, PHASE1_REFERENCE_18_PROBLEM_KEY);
      const existingDay = JourneyService.getDayState(lang, PHASE1_REFERENCE_18_PROBLEM_KEY, 1);
      if (existingMoment || existingDay) {
        navigate(JourneyService.getReturnTarget(lang, PHASE1_REFERENCE_18_PROBLEM_KEY));
        return;
      }

      JourneyService.startNewJourney({
        lang,
        problemKey: PHASE1_REFERENCE_18_PROBLEM_KEY,
        intake: {
          painNow: getPhaseOneGateLabel(gate.key, lang),
          desiredEndState: "",
          oneWord: "",
          entryGateKey: gate.key,
        },
      });

      navigate(`/journey/language-mirror?problemKey=${PHASE1_REFERENCE_18_PROBLEM_KEY}&lang=${lang}`);
    } catch (err) {
      setError(err.message || copy.genderError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="phase1-shell" dir={dir}>
      <div className="phase1-card">
        <p className="phase1-brand">{copy.brand}</p>
        <h1 className="phase1-title phase1-title-multiline">{copy.body}</h1>

        {needsGender && (
          <>
            <p className="phase1-text muted">{getPhaseOneGenderPrompt(lang)}</p>
            <div className="phase1-chip-row">
              <button
                type="button"
                className={`phase1-chip ${selectedGender === "male" ? "active" : ""}`}
                onClick={() => setSelectedGender("male")}
              >
                {copy.genderMale}
              </button>
              <button
                type="button"
                className={`phase1-chip ${selectedGender === "female" ? "active" : ""}`}
                onClick={() => setSelectedGender("female")}
              >
                {copy.genderFemale}
              </button>
            </div>
          </>
        )}

        {error && <div className="phase1-error">{error}</div>}

        <div className="phase1-actions">
          <button type="button" className="phase1-primary" onClick={handleContinue} disabled={saving}>
            {saving ? copy.saving : buttonLabel}
          </button>
          <button
            type="button"
            className="phase1-secondary"
            onClick={() => navigate(`/phase1/select?lang=${lang}`)}
            disabled={saving}
          >
            {copy.back}
          </button>
        </div>
      </div>
    </div>
  );
}
